import {
  action, computed, observable,
} from 'mobx';
import {
  PaymentMethodSetup,
  IPaymentSourceDefinition,
  paymentMethodsSetup,
  paymentMethodsUISetup,
  setupPaymentMethods,
} from '../../application/payment-setup/PaymentServiceSetup';

import { diContainer } from '../../diContainer/diContainer';
import Payment from '../../domain/entities/Payment/Payment';
import Status from '../../constants/Status';
import Config from '../../domain/entities/Config';
import TransactionRepository from '../TransactionRepository';
import PAYMENT_PROVIDERS, { PAYMENT_MODES, PAYMENT_TYPE } from '../../constants/PaymentProviders';
import Transaction from '../../domain/entities/Transaction/Transaction';
import PaymentService from '../../application/services/PaymentService';
import { FIELD_TYPE } from '../../constants/Constants';
import Api, { GiftCardInput } from '../../api';
import { IPaymentSession } from '../../api/IApiResponses';
import { GiftCard } from '../../api/IDataMappings';

export interface IPaymentRepositoryConfig {
  api: Api;
  transactionRepository: TransactionRepository;
  config: Config;
  paymentService: PaymentService;
}

interface ILoadEntry {
  name: string;
  status: Status;
  promise: Promise<void> | null;
}

export class PaymentRepository {
  private paymentUILoadMap: Map<string, ILoadEntry> = new Map();

  private cachedPaymentSourcesDefinitions: IPaymentSourceDefinition[] | null = null;

  private config: Config;

  private transactionRepository: TransactionRepository;

  private paymentService: PaymentService;

  private api: Api;

  constructor(config: IPaymentRepositoryConfig) {
    this.api = config.api;
    this.config = config.config;
    this.transactionRepository = config.transactionRepository;
    this.paymentService = config.paymentService;
  }

  @computed get payment(): Payment {
    return this.transactionRepository?.transaction?.payment;
  }

  @computed
  private get transaction(): Transaction {
    return this.transactionRepository?.transaction;
  }

  @computed
  private get totalCost(): number {
    return this.transaction?.orderSummary?.totalCost;
  }

  @computed
  public get isOrderFullyCoveredByGiftCard(): boolean  { 
    const { areGiftCardsApplied } = this.payment;

    return areGiftCardsApplied && this.totalCost === 0;
  }

  @computed get hasInternationalBilling(): boolean {
    const transaction = this.transactionRepository?.transaction;
    const { [FIELD_TYPE.COUNTRY_ID]: billingCountryId } = transaction?.billingAddress?.attributes;

    return billingCountryId !== this.config?.countryId;
  }

  @observable
  private dependenciesStatus: Status = Status.IDLE;

  @observable
  private initializePromise: Promise<void>;

  @action
  public async initialize(): Promise<void> {
    if (this.dependenciesStatus !== Status.LOADING) {
      this.dependenciesStatus = Status.LOADING;
      this.initializePromise = this.loadPaymentDependenciesByProviders();
    }
    await this.initializePromise;
    this.setupSelectedPayment();
  }

  // Initialize/setup selected payment or default payment
  setupSelectedPayment = async (): Promise<void> => {
    await this.transactionRepository.getTransaction();
    const { selectedPayment, selectedPaymentMode } = this.payment;

    // load selected payment session when total cost is not 0
    // or total cost is 0 but has applied gift card
    if (selectedPayment && (this.totalCost > 0 || this.isOrderFullyCoveredByGiftCard)) {
      this.paymentService.initPayment(selectedPayment, selectedPaymentMode);
    }

    if (!selectedPayment) {
      const { defaultPayment } = this.payment;
      if (!this.isOrderFullyCoveredByGiftCard) {
        this.paymentService.initPayment(
          defaultPayment.providerName as PAYMENT_PROVIDERS,
          defaultPayment.paymentMode as PAYMENT_MODES,
        );
      } else {
        // Do not load the payment session and only set the payment name as gift card covers total
        this.paymentService.setPayment(
          defaultPayment.providerName as PAYMENT_PROVIDERS,
          defaultPayment.paymentMode as PAYMENT_MODES,
        );
      }
    }
  };

  @computed
  public get status(): Status {
    return this.dependenciesStatus;
  }

  public getPaymentSourcesDefinitionsByProvider(
    provider: PAYMENT_PROVIDERS,
  ): IPaymentSourceDefinition {
    return (this.cachedPaymentSourcesDefinitions || []).find(
      item => item.paymentProviderName === provider,
    );
  }

  /**
   * Ensure UI information is loaded for a given provider name
   * @param providerName The name of the payment provider
   */
  public ensureUI = async (providerName: string): Promise<void> => {
    let loadEntry = this.paymentUILoadMap.get(providerName);
    if (!loadEntry) {
      let resolveLoad: () => void | boolean = () => true;
      let rejectLoad: () => void | boolean = () => false;

      const promise = new Promise<void>((resolve, reject): void => {
        resolveLoad = resolve;
        rejectLoad = reject;
      });

      loadEntry = {
        name: providerName,
        promise,
        status: Status.IDLE,
      };
      this.paymentUILoadMap.set(providerName, loadEntry);

      const paymentSourceDefinitions = await this.getPaymentSourceDefinitions();
      const paymentDefinition = paymentSourceDefinitions.find(
        def => def.paymentProviderName === providerName,
      );

      try {
        loadEntry.status = Status.LOADING;

        const setupFunction = this.getPaymentSourceUISetup(paymentDefinition)
          || (() => Promise.reject(new Error('no_setup_ui_function')));

        await setupFunction({
          definition: paymentDefinition,
          diContainer,
        });

        loadEntry.status = Status.LOADED;
        resolveLoad();
      } catch (err) {
        loadEntry.status = Status.ERROR;
        rejectLoad();
      }
    }

    if (!loadEntry.promise) {
      throw new Error(`no ui initialization promise for ${providerName}`);
    }

    return loadEntry.promise;
  };

  // Dynamicaly load payment dependencies/chunks based on transaction payments availability 
  @action
  private async loadPaymentDependenciesByProviders(): Promise<void> {
    try {
      const setupPromises: Promise<void>[] = [];
      setupPaymentMethods();
      await this.transactionRepository.getTransaction();
      const paymentSourcesDefs = await this.getPaymentSourceDefinitions();

      const newProvidersOnly = paymentSourcesDefs.filter(item => {
        return !this.paymentUILoadMap.get(item.paymentProviderName);
      });

      // call the setup function for each payment provider
      newProvidersOnly.forEach(sourceDef => {
        const setupFn = this.getPaymentSourceSetup(sourceDef);
        if (setupFn) {
          const trySetupFn = async (): Promise<void> => {
            try {
              await setupFn({
                definition: { ...sourceDef },
                diContainer,
              });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(
                'error initializing payment source', sourceDef.paymentProviderName, err,
              );
            }

            try {
              await this.ensureUI(sourceDef.paymentProviderName);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error(
                'error initializing ui for payment source', sourceDef.paymentProviderName, err,
              );
            }
          };
          setupPromises.push(trySetupFn());
        } else {
          // eslint-disable-next-line no-console
          console.warn('no payment setup function found for', sourceDef);
        }
      });

      await Promise.all(setupPromises);

      this.dependenciesStatus = Status.LOADED;
    } catch (err) {
      this.dependenciesStatus = Status.ERROR;
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  private getPaymentSourceSetup(
    sourceDef: IPaymentSourceDefinition,
  ): PaymentMethodSetup | undefined {
    return paymentMethodsSetup[sourceDef.paymentProviderName];
  }

  private getPaymentSourceUISetup(
    sourceDef: IPaymentSourceDefinition,
  ): PaymentMethodSetup | undefined {
    return paymentMethodsUISetup[sourceDef.paymentProviderName];
  }

  private getPaymentSourceDefinitions(): IPaymentSourceDefinition[] {
    return (this.payment?.paymentProviders || []).reduce(
      (acc: IPaymentSourceDefinition[], item) => {
        const existingItem = acc.find(
          existingSource => existingSource.paymentProviderName === item.providerName,
        );

        const providerMethods = this.payment.paymentProviders.filter(
          provider => provider.providerName === item.providerName,
        ).map(provider => provider.paymentMode as PAYMENT_MODES).filter(mode => mode);

        return existingItem ? acc : [...acc, {
          methods: providerMethods,
          paymentProviderName: item.providerName as PAYMENT_PROVIDERS,
        }];
      }, [],
    );  
  }

  public createPaymentSession = (
    paymentProvider: PAYMENT_PROVIDERS,
    paymentType?: PAYMENT_TYPE,
    paymentMode?: PAYMENT_MODES,
  ): Promise<IPaymentSession> => {
    return this.api.createPaymentSessionDebounced(paymentProvider, paymentType, paymentMode);
  };

  public getPaymentSession(): Promise<IPaymentSession> {
    return this.api.getPaymentSession();
  }

  public verifyPayment(paymentData?: {}): Promise<IPaymentSession> {
    return this.api.verifyPayment({
      // eslint-disable-next-line @typescript-eslint/naming-convention
      ...paymentData,
      trans_key: this.transactionRepository.transaction.transKey,
    });
  }

  public addGiftCard(giftCard: GiftCardInput): Promise<GiftCard> {
    return this.api.addGiftCard(giftCard);
  }

  public removeGiftCard = async (number: string): Promise<void> => {
    const giftCard = this.transactionRepository.transaction.payment.getGiftCardByNumber(number);
    await this.api.removeGiftCard(giftCard.id);
    this.transactionRepository.transaction.payment.removeGiftCard(giftCard);
  };

}
